import React from "react";
import { useShallow } from "zustand/react/shallow";
import Card from "component/Card/Card";
import useHaptic from "hook/useHaptic";
import { 
    Event, 
    RoomPlayerState, 
    RoomPlayerAction,
    RoomState,
} from "@uno/protocol";
import { AppContext } from "context";
import useStoreRoom from "store/useStoreRoom";
import useStoreRoomPlayer from "store/useStoreRoomPlayer";
import useClick from "hook/useClick";
import { useRoomLayout } from "context/RoomLayoutContext";
import {
    CARD_WIDTH,
    CARD_HEIGHT
} from "types/Card";

const Deck = () => {
    const haptic = useHaptic();
    const { socket } = React.useContext(AppContext);
    const {
        orientation,
        boardShape,
        scales,
        deckRotation,
        alignDeckWithDiscard,
        deckCenterOffset,
    } = useRoomLayout();
    const deckScale = scales.deck;
    const room = useStoreRoom(useShallow(state => ({ id: state.room?.id, state: state.room?.state, deckLength: state.room?.game?.deckCount ?? 0 })));
    const roomPlayer = useStoreRoomPlayer(useShallow(state => ({ state: state.roomPlayer?.state, action: state.roomPlayer?.action })));
    const ref = React.useRef<HTMLDivElement>(null);
    const roomIsPlaying = React.useMemo(() => room?.state === RoomState.Playing, [room?.state]);
    const myTurn = React.useMemo(() => roomPlayer && [RoomPlayerState.Active, RoomPlayerState.AwaitingAction].includes(roomPlayer?.state as RoomPlayerState) && roomPlayer?.action !== RoomPlayerAction.ChoosingColor, [roomPlayer?.state, roomPlayer?.action]);
    const canDrawCard = roomIsPlaying && myTurn;
    
    const deckRatio = React.useMemo(() => {
        return Math.max(1, Math.round(Math.min(8, room.deckLength / 6)));
    }, [room.deckLength]);
    
    const handleClick = React.useCallback(() => {
        if(!canDrawCard) {
            return;
        }

        if(roomPlayer?.state === RoomPlayerState.AwaitingAction) {
            return socket?.emit(Event.RoomPlayerSkipTurn, room?.id);
        }

        socket?.emit(Event.RoomPlayerDrawCard, room?.id);
    }, [canDrawCard, roomPlayer?.state, socket, room?.id]);

    useClick(handleClick, ref, { onHaptic: haptic });

    return (
        <div
            id="deck"
            ref={ref}
            className={`absolute ${canDrawCard ? "cursor-pointer" : ""}`}
            style={orientation === "landscape" ? {
                top: "50%",
                left: `calc(50% - ${deckCenterOffset ?? 0}px)`,
                margin: 0,
                transform: "translate(-50%, -50%)",
            } : {
                top: boardShape === "circle" ? "101%" : "98%",
                left: boardShape === "circle" ? "2rem" : "1rem",
                margin: 0,
                transform: "none",
            }}
        >
            <div
                id="deck-container"
                style={{
                    ...(alignDeckWithDiscard ? {
                        width: `${CARD_WIDTH}px`,
                        height: `${CARD_HEIGHT}px`,
                    } : {}),
                    transform: `scale(${deckScale}) rotate(${deckRotation}deg)`,
                }}
            >
                {canDrawCard && (
                    <div
                        className="deck-glow absolute z-0 inset-0 rounded-[10px]"
                        style={{
                            width: `${CARD_WIDTH}px`,
                            height: `${CARD_HEIGHT}px`,
                        }}
                    />
                )}
                
                {Array.from({ length: deckRatio }).map((_, index) => (
                    <div key={index} className={"absolute"} style={{
                        transform: `translateX(${index * 2}px) translateY(${orientation === "portrait" ? index * -2 : index * 2}px)`,
                        zIndex: deckRatio - index,
                    }}>
                        <Card id={`deck-${index}`} type="BACK" isHoverable={false} />
                        {index > 0 && <div className="absolute inset-0 rounded-[12px] bg-black pointer-events-none" style={{ opacity: index * 0.07 }}></div>}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default React.memo(Deck);
